import { CognitoUser, CognitoUserPool, ICognitoUserPoolData } from 'amazon-cognito-identity-js';
import axios, { AxiosError } from 'axios';
import { AwsLoginErrors, AuthAttribute } from '../constants/telosair-interfaces';
import axiosInstance from '../_helpers/interceptor';
import { DEV_DOMAIN_URL_LOGIN, DEV_NEW_DOMAIN_URL } from '../environment';

const UserPoolId = process.env.REACT_APP_USER_POOL_ID || ' temmp ';
const ClientId = process.env.REACT_APP_CLIENT_ID || ' client ';

const poolData: ICognitoUserPoolData = {
  UserPoolId,
  ClientId
};

const userPool = new CognitoUserPool(poolData);

/**
 * Logs in the user using Cognito authentication and then logs in to the application server.
 * @param userName - The username of the user.
 * @param password - The password of the user.
 * @returns - Callback.
 */
async function login(userName: string, password: string): Promise<AuthAttribute> {
  sessionStorage.clear();
  const authenticationData = {
    userName,
    password
  };

  try {
    const response = await axiosInstance.post(
      `${DEV_DOMAIN_URL_LOGIN}/auth/login`,
      authenticationData
    );
    if (response.data.challengeName === 'NEW_PASSWORD_REQUIRED') {
      return {
        error: AwsLoginErrors.newUser,
        data: { token: response.data.session }
      };
    }
    sessionStorage.setItem('token', response.data.accessToken);
    sessionStorage.setItem('refresh_token', response.data.refreshToken);
    return {
      error: false
    };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.log(e);
    if (e instanceof AxiosError) {
      const message = e?.response?.data.message;
      if (message?.includes('NotAuthorizedException')) {
        return {
          error: AwsLoginErrors.invalidCredentials
        };
      }
      if (message?.includes('UserNotConfirmedException')) {
        return {
          error: AwsLoginErrors.unconfirmed
        };
      }
    }
    return {
      error: AwsLoginErrors.other
    };
  }
}

/**
 * Function to handle resend confimation code.
 * @param username - Username.
 * @param cb - Callback.
 */
function resendConfirmationCode(username: string, cb: (failed: boolean) => void): void {
  const userData = {
    Username: username,
    Pool: userPool
  };

  const cognitoUser = new CognitoUser(userData);

  cognitoUser.resendConfirmationCode((err) => {
    if (err) {
      // eslint-disable-next-line no-console
      console.log(err);
      cb(true);
      return;
    }
    cb(false);
  });
}

/**
 * Function to handle verify confimation code.
 * @param userName - Username.
 * @param token - OTP.
 * @returns - Callback.
 */
async function verifyConfirmationCode(userName: string, token: string): Promise<AuthAttribute> {
  const authenticationData = {
    userName,
    token
  };
  try {
    await axios.post(`${DEV_NEW_DOMAIN_URL}/public/user/register/confirm`, authenticationData);
    return {
      error: false
    };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.log(e);
    if (e instanceof AxiosError) {
      const message = e?.response?.data.message;
      if (message?.includes('CodeMismatchException')) {
        return {
          error: AwsLoginErrors.codeMismatch
        };
      }
      if (e.response?.status === 404) {
        return {
          error: AwsLoginErrors.notFound
        };
      }
    }
    return {
      error: AwsLoginErrors.other
    };
  }
}

/**
 * Function to handle verify confimation code.
 * @param userName - Username.
 * @returns - Callback.
 */
async function forgotPassword(userName: string): Promise<AuthAttribute> {
  const authenticationData = {
    userName
  };
  try {
    await axios.post(
      `${DEV_NEW_DOMAIN_URL}/public/user/forgot-password/send-code`,
      authenticationData
    );
    return {
      error: false
    };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.log(e);
    if (e instanceof AxiosError) {
      if (e.response?.status === 404) {
        return {
          error: AwsLoginErrors.notFound
        };
      }
    }
    return {
      error: AwsLoginErrors.other
    };
  }
}

/**
 * Function to handle verify confimation code.
 * @param userName - Username.
 * @param token - OTP.
 * @param password - Password.
 * @returns - Callback.
 */
async function resetPassword(
  userName: string,
  token: string,
  password: string
): Promise<AuthAttribute> {
  const authenticationData = {
    userName,
    token,
    password,
    confirmPassword: password
  };
  try {
    await axios.post(
      `${DEV_NEW_DOMAIN_URL}/public/user/forgot-password/confirm`,
      authenticationData
    );
    return {
      error: false
    };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.log(e);
    if (e instanceof AxiosError) {
      if (e.response?.status === 404) {
        return {
          error: AwsLoginErrors.notFound
        };
      }
      const message = e?.response?.data.message;
      if (message?.includes('ExpiredCodeException')) {
        return {
          error: AwsLoginErrors.expiredCode
        };
      }
    }
    return {
      error: AwsLoginErrors.other
    };
  }
}

/**
 * Function to handle verify confimation code.
 * @param userName - Username.
 * @param temporaryPassword - Old password.
 * @param password - New password.
 * @param sessionToken - Session token sent by the API.
 * @returns - Callback.
 */
async function resetTemporaryPassword(
  userName: string,
  temporaryPassword: string,
  password: string,
  sessionToken: string
): Promise<AuthAttribute> {
  const authenticationData = {
    userName,
    temporaryPassword,
    password,
    confirmPassword: password,
    sessionToken
  };
  try {
    await axios.post(`${DEV_NEW_DOMAIN_URL}/public/user/respond-to-challenge`, authenticationData);
    return {
      error: false
    };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.log(e);
    if (e instanceof AxiosError) {
      const message = e?.response?.data.message;
      if (message?.includes('NotAuthorizedException')) {
        return {
          error: AwsLoginErrors.invalidCredentials
        };
      }
      if (e.response?.status === 404) {
        return {
          error: AwsLoginErrors.notFound
        };
      }
    }
    return {
      error: AwsLoginErrors.other
    };
  }
}

/**
 * Function to handle verify confimation code.
 * @param previousPassword - Old password.
 * @param password - New password.
 * @returns - Callback.
 */
async function changePassword(previousPassword: string, password: string): Promise<AuthAttribute> {
  const authenticationData = {
    password,
    confirmPassword: password,
    previousPassword
  };
  try {
    await axiosInstance.post(`${DEV_NEW_DOMAIN_URL}/user/change-password`, authenticationData);
    return {
      error: false
    };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.log(e);
    if (e instanceof AxiosError) {
      const message = e?.response?.data.message;
      if (message?.includes('NotAuthorizedException')) {
        return {
          error: AwsLoginErrors.invalidCredentials
        };
      }
      if (e.response?.status === 404) {
        return {
          error: AwsLoginErrors.notFound
        };
      }
    }
    return {
      error: AwsLoginErrors.other
    };
  }
}

/**
 * Function to refresh auth token.
 * @returns - Callback.
 */
async function refreshToken(): Promise<AuthAttribute> {
  try {
    const token = sessionStorage.getItem('refresh_token');
    if (token) {
      const { data } = await axios.post(
        `${DEV_DOMAIN_URL_LOGIN}/auth/refresh-token`,
        { refreshToken: token },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );
      sessionStorage.setItem('token', data.accessToken);
      return {
        error: false,
        data
      };
    }
    return {
      error: AwsLoginErrors.other
    };
  } catch (e: any) {
    // eslint-disable-next-line no-console
    console.log(e);
    if (e instanceof AxiosError) {
      const message = e?.response?.data.message;
      if (message?.includes('NotAuthorizedException')) {
        return {
          error: AwsLoginErrors.invalidCredentials
        };
      }
      if (e.response?.status === 404) {
        return {
          error: AwsLoginErrors.notFound
        };
      }
    }
    return {
      error: AwsLoginErrors.other
    };
  }
}

const authenticateService = {
  login,
  resendConfirmationCode,
  verifyConfirmationCode,
  forgotPassword,
  resetPassword,
  resetTemporaryPassword,
  changePassword,
  refreshToken
};

export default authenticateService;
